/*
 * $Id:: index.js 2021/02/26 06:09 otsolka
 *
 *        . * .
 *      * RRRR  *   Copyright (c) 2012-2021 EUIPO: European Intelectual
 *     .  RR  R  .  Property Organization (trademarks and designs).
 *     *  RRR    *
 *      . RR RR .   ALL RIGHTS RESERVED
 *       *. _ .*
 *
 *  The use and distribution of this software is under the restrictions exposed in 'license.txt'
 */

import React, { useState, useEffect } from 'react';

import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import Input from '@material-ui/core/Input';
import InputAdornment from '@material-ui/core/InputAdornment';
import InputLabel from '@material-ui/core/InputLabel';
import { makeStyles } from '@material-ui/core/styles';

import { ReactComponent as CancelIcon } from '../../assets/images/cancel_24px.svg';
import InfoIcon from '../../assets/images/info_icon.svg';
import { ReactComponent as SearchIcon } from '../../assets/images/searchIcon.svg';
import { t } from '../../providers';

const useStyles = makeStyles((theme) => ({
    searchInput: {
        display: 'flex',
        flexFlow: 'row nowrap',
        alignItems: 'center',
        '& input': {
            '&::placeholder': {
                color: '#5D5D5D',
                fontSize: '0.875rem',
                letterSpacing: '-0.2px',
                fontFamily: theme.typography.button.fontFamily,
            },
            '&:focus-visible': {
                backgroundColor: 'rgba(209, 224, 194, 0.2)',
                outline: 'none',
                border: '2px dashed #4F7E1D',
                borderRadius: '3px',
                paddingLeft: '7px',
                marginLeft: '0px',
            },
        },
    },
    searchLabel: {
        fontSize: '0.75rem',
        fontWeight: '600',
        color: theme.palette.text.mainGreen,
        marginBottom: '0px',
        letterSpacing: '-0.2px',
        lineHeight: '15.96px',
    },
    textInput: {
        height: '32px',
        width: '100%',
        background: '#ffffff',
        border: '1px solid #B1B1B1',
        borderRadius: '3px',
        '& input': {
            '&::placeholder': {
                paddingLeft: '2px',
                opacity: '1',
                color: theme.palette.text.chipgrey,
                fontSize: '0.875rem',
                fontWeight: '400',
                letterSpacing: '-0.2px',
                lineHeight: '19.88px',
            },
        },
        '@media (max-width:600px)': {
            width: '79%',
        },
        '& .MuiInputAdornment-positionEnd': {
            marginLeft: '0px',
        },
    },
    searchButton: {
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
        height: '32px',
        color: theme.palette.text.mainGreen,
        border: '1px solid' + theme.palette.text.mainGreen,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderRadius: '2px',
        lineHeight: '12px',
        '&:hover': {
            backgroundColor: theme.palette.text.primaryLight,
        },
    },
    searchButtonImpersonation: {
        minWidth: '6rem',
    },
    searchButtonOther: {
        minWidth: '4.5rem',
    },
    disabledSearch: {
        cursor: 'not-allowed',
    },
    cancelBtn: {
        padding: '5px 10px',
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
    searchIcon: {
        pointerEvents: 'none',
        padding: '10px',
    },
    searchSection: {
        width: '1015px',
        '@media (max-width:1506px)': {
            paddingBottom: '0px',
        },
        '@media (max-width:1357px)': {
            width: '98%',
        },
        '@media (max-width:1100px)': {
            width: '99%',
        },
    },
    orText: {
        fontStyle: 'normal',
        fontWeight: '600',
        fontSize: '0.875rem',
        lineHeight: '171%',
        letterSpacing: '0.5px',
        textTransform: 'uppercase',
        color: '#5D5D5D',
        padding: '0px 24px',
    },
    fullListButton: {
        height: '32px',
        minWidth: '8rem',
        color: theme.palette.text.mainGreen,
        border: '1px solid' + theme.palette.text.mainGreen,
        textTransform: 'uppercase',
        letterSpacing: '0.5px',
        borderRadius: '2px',
        lineHeight: '12px',
    },
    infoIcn: {
        '&:focus-visible': {
            backgroundColor: 'rgba(209, 224, 194, 0.2)',
            outline: 'none',
            border: '2px dashed #4F7E1D',
            borderRadius: '3px',
        },
    },
}));

const SearchFilter = ({
    onSearch,
    searchQuery,
    onClear,
    toggleInfoMessage = () => {},
    page = '',
    getUserList = () => {},
    searchLabel = 'search.in.application.text',
    showGetFullListButton = true,
    hideInfo = false,
}) => {
    const classes = useStyles();

    const [search, setSearch] = useState('');

    useEffect(() => {
        setSearch(searchQuery);
    }, [searchQuery]);

    const handleSearchChange = (event) => {
        if (searchQuery && search.length >= 1 && event.target.value.length === 0) {
            onClear();
        }
        setSearch(event.target.value);
    };

    const doSearch = () => onSearch(search);

    const onClearSearch = () => {
        setSearch('');
        if (searchQuery) {
            onClear();
        }
        const searchInput = document.getElementById('search');
        searchInput.focus();
    };

    return (
        <div className={classes.searchSection}>
            <InputLabel htmlFor="search" className={classes.searchLabel}>
                <span style={{ marginRight: '5px' }}>{t(searchLabel)}</span>
                {hideInfo && (
                    <IconButton
                        onClick={() => {
                            toggleInfoMessage(false);
                        }}
                        style={{ padding: 'unset' }}
                        aria-label={t('open.info.button')}
                        className={classes.infoIcn}>
                        <img src={InfoIcon} alt={t('info.title.text')} aria-hidden="true" focusable="false" />
                    </IconButton>
                )}
            </InputLabel>
            <div className={classes.searchInput}>
                <Input
                    id="search"
                    className={classes.textInput}
                    placeholder={t('search.filter.placeholder')}
                    name="search"
                    fullWidth
                    endAdornment={
                        <InputAdornment position="end">
                            {!search ? (
                                <SearchIcon aria-hidden="true" focusable="false" className={classes.searchIcon} />
                            ) : (
                                <IconButton
                                    aria-label={t('clear.search')}
                                    disableFocusRipple
                                    className={classes.cancelBtn}
                                    id={'cancelButton'}
                                    onClick={onClearSearch}>
                                    <CancelIcon alt={t('clear')} aria-hidden="true" focusable="false" />
                                </IconButton>
                            )}
                        </InputAdornment>
                    }
                    onChange={handleSearchChange}
                    value={search}
                />
                <Button
                    id={'searchButton'}
                    onClick={doSearch}
                    className={[
                        classes.searchButton,
                        page === 'impersonation' ? classes.searchButtonImpersonation : classes.searchButtonOther,
                        search.length < 3 ? classes.disabledSearch : '',
                    ].join(' ')}
                    disableRipple
                    disableFocusRipple>
                    {t('search')}
                </Button>
                {page === 'impersonation' && showGetFullListButton && (
                    <>
                        <span className={classes.orText} aria-hidden="true" focusable="false">
                            {t('or.text.label').toUpperCase()}
                        </span>
                        <Button id={'searchButton'} onClick={getUserList} className={classes.fullListButton} disableRipple disableFocusRipple>
                            {t('get.full.list')}
                        </Button>
                    </>
                )}
            </div>
        </div>
    );
};

export default SearchFilter;
